import { Application, Feature } from '@frontend/functionality/types';
import { CommonMessage } from '@frontend/lang';
import { Logger } from '@frontend/logger';
import { Package } from '@frontend/package/types';
import { SettingKey } from '@frontend/setting/types';
import { TransactionStateName } from '@frontend/transaction/types';
import { type ReactNode, useCallback, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { useLocation, useParams } from 'react-router-dom';

import { useAppSelector } from '../../redux/store';
import { useKioskNavigate } from '../../utils/kiosk-navigation';
import { useLicenseRepository } from '../../utils/license-repository';
import { BulkMoveAssignment } from './bulk-move-colli-units';
import { BulkMoveLocationState } from './bulk-move-location-state';
import { dedupePackagesBySlotId, groupAssignmentsByTransaction, moveBulkTransactions } from './bulk-move-transactions';
import {
    triggerOpenSlotForDropPackages,
    triggerOpenSlotForRemovalPackages,
    waitForOpenableDropPackages,
    waitForOpenableRemovalPackages
} from './wait-for-openable-drop-package';

export type BulkMoveConfirmPhase = 'confirm' | 'source-opening' | 'drop-opening' | 'done';

export interface BulkMoveConfirmViewProps {
    phase: BulkMoveConfirmPhase;
    assignments: BulkMoveAssignment[];
    destinationSlotDisplay: string | null;
    openingPackages: Package[];
    isSameSpot: boolean;
    isSubmitting: boolean;
    error: ReactNode | null;
    successCount: number;
    failureCount: number;
    canConfirm: boolean;
    handleConfirm: () => Promise<void>;
    handleOpeningContinue: () => Promise<void>;
    handleBack: () => void;
}

export const useBulkMoveConfirm = (): BulkMoveConfirmViewProps => {
    const { destSpotId } = useParams<{ destSpotId: string }>();
    const navigate = useKioskNavigate();
    const location = useLocation();
    const navigationState = useAppSelector((state) => state.navigation);
    const { getFunctionality, getFunctionalitySetting } = useLicenseRepository();

    const state = (location.state as BulkMoveLocationState | null) ?? {};
    const assignments = state.assignments ?? [];

    const accountId = navigationState.user?.accountId ?? navigationState.iot?.account_id;
    const actorUserId = navigationState.user?.id;
    const triggerLocationSpotId = navigationState.spot?.id;
    const isSameSpot = !!destSpotId && destSpotId === triggerLocationSpotId;

    const bulkFunctionality = getFunctionality(Application.PUDO, Feature.BULK_MOVE_TRANSACTION);
    const bulkSettings = getFunctionalitySetting(bulkFunctionality?.id ?? '');
    const dropOffPackageWorkflowId = bulkSettings?.[SettingKey.DROP_OFF_MOVED_PACKAGE_WORKFLOW_ID] as string | undefined;
    const pickUpPackageWorkflowId = bulkSettings?.[SettingKey.PICK_UP_MOVED_PACKAGE_WORKFLOW_ID] as string | undefined;
    const removePackageWorkflowId = bulkSettings?.[SettingKey.REMOVE_PACKAGE_WORKFLOW_ID] as string | undefined;

    const hasPickUpPending = useMemo(() => assignments.some((a) => a.transactionState === TransactionStateName.TRANSACTION_PICK_UP_PENDING), [assignments]);

    const workflowsReady = !!dropOffPackageWorkflowId && !!pickUpPackageWorkflowId && (!hasPickUpPending || !!removePackageWorkflowId);

    const [phase, setPhase] = useState<BulkMoveConfirmPhase>('confirm');
    const [openingPackages, setOpeningPackages] = useState<Package[]>([]);
    const [dropPackages, setDropPackages] = useState<Package[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<ReactNode | null>(null);
    const [successCount, setSuccessCount] = useState(0);
    const [failureCount, setFailureCount] = useState(0);

    const finishSameSpotFlow = useCallback(
        (failures: number) => {
            setOpeningPackages([]);
            // Happy path: skip "Move complete" and return to the list. Partial failures need a summary.
            if (failures > 0) {
                setPhase('done');
                return;
            }
            navigate('/spots/layout/pudo/bulk-move-transactions');
        },
        [navigate]
    );

    const startDestinationDropOpening = useCallback(
        async (packages: Package[], failures: number) => {
            if (!accountId || packages.length === 0) {
                finishSameSpotFlow(failures);
                return;
            }

            // Destination door UI before wait/trigger (MQTT + visible SLOT switch).
            flushSync(() => {
                setOpeningPackages(dedupePackagesBySlotId(packages));
                setPhase('drop-opening');
            });

            const openable = await waitForOpenableDropPackages(accountId, packages);
            await triggerOpenSlotForDropPackages(accountId, openable, {
                location: triggerLocationSpotId,
                actor: actorUserId
            });

            if (openable.length > 0) {
                flushSync(() => {
                    setOpeningPackages(dedupePackagesBySlotId(openable));
                });
            }
        },
        [accountId, actorUserId, finishSameSpotFlow, triggerLocationSpotId]
    );

    const startSourceRemovalOpening = useCallback(
        async (packages: Package[]) => {
            if (!accountId || packages.length === 0) return;

            const openable = await waitForOpenableRemovalPackages(accountId, packages);
            await triggerOpenSlotForRemovalPackages(accountId, openable, {
                location: triggerLocationSpotId,
                actor: actorUserId
            });

            const uiPackages = dedupePackagesBySlotId(openable.length > 0 ? openable : packages);
            flushSync(() => {
                setOpeningPackages(uiPackages);
                setPhase('source-opening');
            });
        },
        [accountId, actorUserId, triggerLocationSpotId]
    );

    const handleConfirm = useCallback(async () => {
        if (!accountId || assignments.length === 0 || !workflowsReady || isSubmitting) return;

        setIsSubmitting(true);
        setError(null);

        if (hasPickUpPending) {
            const preview = dedupePackagesBySlotId(
                assignments
                    .filter((a) => a.transactionState === TransactionStateName.TRANSACTION_PICK_UP_PENDING && a.sourceSlotId)
                    .map((a) => ({ slot_id: a.sourceSlotId }) as Package)
            );
            if (preview.length > 0) {
                flushSync(() => {
                    setOpeningPackages(preview);
                    setPhase('source-opening');
                });
            }
        }

        try {
            const grouped = groupAssignmentsByTransaction(assignments);
            const inputs = [...grouped.entries()].map(([transactionId, txsAssignments]) => ({
                accountId,
                transactionId,
                transactionState: txsAssignments[0].transactionState,
                assignments: txsAssignments.map((a) => ({ colliId: a.colliId, destination: a.destination })),
                dropOffPackageWorkflowId: dropOffPackageWorkflowId!,
                pickUpPackageWorkflowId: pickUpPackageWorkflowId!,
                removePackageWorkflowId,
                actorUserId,
                triggerLocationSpotId
            }));

            const result = await moveBulkTransactions(inputs);
            const nextSuccessCount = result.successes.length;
            const nextFailureCount = result.failures.length;
            setSuccessCount(nextSuccessCount);
            setFailureCount(nextFailureCount);

            if (nextSuccessCount === 0) {
                setError(CommonMessage.ERRORS.DEFAULT);
                setPhase('confirm');
                setOpeningPackages([]);
                return;
            }

            // Keep the full drop list for PACKAGE_OPEN_SLOT; UI dedupes separately.
            setDropPackages(result.dropPackages);

            if (result.removalPackages.length > 0) {
                await startSourceRemovalOpening(result.removalPackages);
                return;
            }

            if (result.sourceOpeningPackages.length > 0) {
                setOpeningPackages(dedupePackagesBySlotId(result.sourceOpeningPackages));
                setPhase('source-opening');
                return;
            }

            if (isSameSpot && result.dropPackages.length > 0) {
                await startDestinationDropOpening(result.dropPackages, nextFailureCount);
                return;
            }

            if (!isSameSpot) {
                navigate('/spots/layout/pudo/awaiting-drop-off');
                return;
            }

            finishSameSpotFlow(nextFailureCount);
        } catch (err) {
            Logger.error('Bulk move failed', err);
            setError(CommonMessage.ERRORS.DEFAULT);
            setPhase('confirm');
            setOpeningPackages([]);
        } finally {
            setIsSubmitting(false);
        }
    }, [
        accountId,
        actorUserId,
        assignments,
        dropOffPackageWorkflowId,
        finishSameSpotFlow,
        hasPickUpPending,
        isSameSpot,
        isSubmitting,
        navigate,
        pickUpPackageWorkflowId,
        removePackageWorkflowId,
        startDestinationDropOpening,
        startSourceRemovalOpening,
        triggerLocationSpotId,
        workflowsReady
    ]);

    const handleOpeningContinue = useCallback(async () => {
        if (phase === 'source-opening') {
            if (isSameSpot && dropPackages.length > 0) {
                await startDestinationDropOpening(dropPackages, failureCount);
                return;
            }
            navigate('/spots/layout/pudo/awaiting-drop-off');
            return;
        }

        if (phase === 'drop-opening') {
            finishSameSpotFlow(failureCount);
            return;
        }

        navigate('/spots/layout/pudo/bulk-move-transactions');
    }, [dropPackages, failureCount, finishSameSpotFlow, isSameSpot, navigate, phase, startDestinationDropOpening]);

    const handleBack = useCallback(() => {
        if (phase !== 'confirm') return;
        navigate(-1);
    }, [navigate, phase]);

    return {
        phase,
        assignments,
        destinationSlotDisplay: assignments[0]?.destinationSlotDisplay ?? null,
        openingPackages,
        isSameSpot,
        isSubmitting,
        error: error ?? (!workflowsReady && phase === 'confirm' ? CommonMessage.ERRORS.DEFAULT : null),
        successCount,
        failureCount,
        canConfirm: !!accountId && assignments.length > 0 && workflowsReady && !isSubmitting && phase === 'confirm',
        handleConfirm,
        handleOpeningContinue,
        handleBack
    };
};
